import { Component, inject, signal } from '@angular/core';
import { formControl, Validators } from '@al00x/forms';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  CacTableComponent,
  CacButtonComponent,
  formBuilder,
  table,
} from '@controladad/ng-base';
import { GovernorateEntity, AreaEntity, GovernorateItem } from '@models';
import { GovernorateApiService } from '../../../services';
import { extractName } from '../../../helpers';
import { of, tap, take } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { AuthStore } from '../../../states';

// Import dialog$ from the global scope
declare const dialog$: any;
declare const snackbar$: any;

export interface GovernorateDialogData {
  governorate?: GovernorateEntity;
  governorateItem?: GovernorateItem; // For the name and basic info
}

export type GovernorateDialogResult = boolean;

@Component({
  selector: 'lib-governorate-dialog',
  imports: [
    CacDialogLayoutComponent,
    CacFormBuilderComponent,
    CacTableComponent,
    CacButtonComponent,
    MatIcon,
    NgIf
  ],
  templateUrl: './governorate-dialog.component.html',
  styleUrl: './governorate-dialog.component.scss',
})
export class GovernorateDialogComponent extends CacBaseDialogComponent<
  GovernorateDialogData,
  GovernorateDialogResult
> {
  private readonly governorateApi = inject(GovernorateApiService);
  private readonly authStore = inject(AuthStore);

  // Step management
  currentStep = signal<number>(0);
  stepValidationErrors = signal<string[]>([]);
  submitError = signal<string | undefined>(undefined);

  // Track if governorate has been saved (for new governorates)
  governorateSaved = signal<boolean>(false);
  savedGovernorateId = signal<number | undefined>(undefined);

  // Steps configuration
  steps = signal([
    { label: 'Governorate Details', key: 'governorate' },
    { label: 'Areas', key: 'areas' }
  ]);

  form = formBuilder({
    cols: 2,
    inputs: {
      englishName: {
        control: formControl<string>(this.data?.governorateItem?.name?.english || '', Validators.required),
        label: 'English Name',
      },
      arabicName: {
        control: formControl<string>(this.data?.governorateItem?.name?.arabic || '', Validators.required),
        label: 'Arabic Name',
      },
      price: {
        control: formControl<number>(this.data?.governorate?.price || this.data?.governorateItem?.price || 0, [Validators.required, Validators.min(0)]),
        label: 'Shipping Fee',
        colspan: 2,
      },
    },
  });

  areasTable = table({
    itemsFn: () => {
      const governorateId = this.getGovernorateId();
      return governorateId ? this.governorateApi.getAreas(governorateId) : of([]);
    },
    view: { itemName: 'Area' },
    columns: {
      id: { label: 'ID' },
      'name.english': { label: 'English Name', sortable: true, filterable: true },
      'name.arabic': { label: 'Arabic Name', sortable: true, filterable: true },
    },
    actions: [
      {
        type: 'icon' as const,
        content: 'edit',
        action: (area: AreaEntity) => {
          const governorateId = this.getGovernorateId();
          return governorateId ? dialog$.area({ area, governorateId }).afterSubmit() : of(false);
        },
      },
      ...(!this.authStore.isRestrictedAdmin() ? [{
        type: 'icon' as const,
        content: 'trash',
        action: (area: AreaEntity) => {
          return dialog$
            .deletePrompt(area.name?.english || `Area ${area.id}`)
            .action(() => this.governorateApi.deleteArea(area.id));
        },
      }] : []),
    ],
    events: {
      add: () => {
        // Force get the latest governorate ID
        const savedId = this.savedGovernorateId();
        const dataId = this.data.governorate?.id || this.data.governorateItem?.id;
        const governorateId = savedId || dataId;

        if (governorateId) {
          return dialog$.area({ governorateId }).afterSubmit();
        } else {
          snackbar$.show('Please save the governorate first before adding areas');
          return of(false);
        }
      }
    },
  });

  constructor() {
    super();

    // Initialize saved state for existing governorates
    if (this.data?.governorate?.id || this.data?.governorateItem?.id) {
      this.governorateSaved.set(true);
      this.savedGovernorateId.set(this.data.governorate?.id || this.data.governorateItem?.id);
    }
  }

  // Helper method to get current governorate ID
  private getGovernorateId(): number | undefined {
    return this.savedGovernorateId() || this.data.governorate?.id || this.data.governorateItem?.id;
  }

  // Step navigation methods
  canProceedToNextStep(): boolean {
    return this.validateCurrentStep();
  }

  canGoToPreviousStep(): boolean {
    return this.currentStep() > 0;
  }

  canSave(): boolean {
    return this.currentStep() === this.steps().length - 1;
  }

  isAreasStepDisabled(): boolean {
    // Areas step is disabled if:
    // 1. We're adding a new governorate AND it hasn't been saved yet
    // 2. OR there are validation errors in the governorate step
    const isNewGovernorate = !this.data?.governorate?.id && !this.data?.governorateItem?.id;
    return isNewGovernorate && !this.governorateSaved();
  }

  nextStep(): void {
    if (this.canProceedToNextStep() && this.currentStep() < this.steps().length - 1) {
      // If moving from governorate step to areas step, always save the governorate (create or update)
      if (this.currentStep() === 0) {
        this.saveGovernorateAndProceed();
      } else {
        this.currentStep.set(this.currentStep() + 1);
        this.stepValidationErrors.set([]);
      }
    }
  }

  // Save governorate and proceed to next step (fallback method)
  private saveGovernorateAndProceed(): void {
    const value = this.form.getValue();
    if (!value) return;

    const fields = {
      name: extractName(value),
      price: value.price
    };

    const governorateId = this.getGovernorateId();
    const request = governorateId
      ? this.governorateApi.updateGovernorate({ governorateId, fields })
      : this.governorateApi.create({ fields });

    request.pipe(
      take(1)
    ).subscribe({
      next: (response: any) => {
        // For new governorates, save the returned ID
        if (!governorateId && response) {
          this.savedGovernorateId.set(response);
        }
        this.governorateSaved.set(true);
        snackbar$.show('Governorate saved successfully');


        this.currentStep.set(1); // Move to areas step
        this.stepValidationErrors.set([]);
        // Refresh the areas table to reflect the new governorate ID
        this.areasTable.refresh();
      },
      error: (error) => {
        this.submitError.set('Failed to save governorate. Please try again.');
        console.error('Error saving governorate:', error);
      }
    });
  }

  // Method to handle opening area dialog
  openAreaDialog() {
    const governorateId = this.getGovernorateId();
    if (governorateId) {
      return dialog$.area({ governorateId }).afterSubmit();
    } else {
      // If no governorate ID, show a message or handle appropriately
      snackbar$.show('Please save the governorate first before adding areas');
      return of(false);
    }
  }

  // Handle the main action button (Next or Save) - for custom footer
  handleMainAction(): void {
    if (this.canSave()) {
      // On areas step, just close the dialog since governorate is already saved
      this.submit(true);
    } else {
      // For Next button, validate current step first
      if (this.canProceedToNextStep()) {
        this.nextStep();
      }
    }
  }

  // Handle the main action button (Next or Save) - with proper loading state
  onMainAction(event: ButtonClickEvent): void {
    if (this.canSave()) {
      // On areas step, just close the dialog since governorate is already saved
      this.submit(true);
      event.setLoading(false);
    } else {
      // For Next button, validate current step first
      if (this.canProceedToNextStep()) {
        // If moving from governorate step to areas step, always save the governorate first (create or update)
        if (this.currentStep() === 0) {
          this.saveGovernorateAndProceedWithEvent(event);
        } else {
          this.currentStep.set(this.currentStep() + 1);
          this.stepValidationErrors.set([]);
          event.setLoading(false);
        }
      } else {
        event.setLoading(false);
      }
    }
  }

  // Save governorate and proceed to next step with proper event handling
  private saveGovernorateAndProceedWithEvent(event: ButtonClickEvent): void {
    const value = this.form.getValue();
    if (!value) {
      event.setLoading(false);
      return;
    }

    const fields = {
      name: extractName(value),
      price: value.price
    };

    const governorateId = this.getGovernorateId();
    const request = governorateId
      ? this.governorateApi.updateGovernorate({ governorateId, fields })
      : this.governorateApi.create({ fields });

    request.pipe(event.pipe()).subscribe({
      next: (response: any) => {
        // For new governorates, save the returned ID
        if (!governorateId && response?.id) {
          this.savedGovernorateId.set(response.id);
        }
        this.governorateSaved.set(true);
        snackbar$.show('Governorate saved successfully');

        this.currentStep.set(1); // Move to areas step
        this.stepValidationErrors.set([]);
        // Refresh the areas table to reflect the new governorate ID
        this.areasTable.refresh();
      },
      error: (error) => {
        this.submitError.set('Failed to save governorate. Please try again.');
        console.error('Error saving governorate:', error);
      }
    });
  }

  // Close dialog method
  closeDialog(): void {
    this.dialog.close();
  }

  previousStep(): void {
    if (this.canGoToPreviousStep()) {
      this.currentStep.set(this.currentStep() - 1);
      this.stepValidationErrors.set([]);
    }
  }

  goToStep(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.steps().length) {
      // Don't allow navigation to areas step if it's disabled
      if (stepIndex === 1 && this.isAreasStepDisabled()) {
        return;
      }

      // Validate all previous steps before allowing navigation
      let canNavigate = true;
      for (let i = 0; i < stepIndex; i++) {
        if (!this.validateStep(i)) {
          canNavigate = false;
          break;
        }
      }

      if (canNavigate) {
        this.currentStep.set(stepIndex);
        this.stepValidationErrors.set([]);
      }
    }
  }

  // Step validation methods
  validateCurrentStep(): boolean {
    return this.validateStep(this.currentStep());
  }

  validateStep(stepIndex: number): boolean {
    const errors: string[] = [];
    const currentSteps = this.steps();

    if (stepIndex >= currentSteps.length) {
      return true;
    }

    const stepKey = currentSteps[stepIndex].key;

    switch (stepKey) {
      case 'governorate':
        return this.validateGovernorateStep(errors);
      case 'areas':
        return this.validateAreasStep(errors);
      default:
        return true;
    }
  }

  private validateGovernorateStep(errors: string[]): boolean {
    this.form.formGroup.markAllAsTouched();

    const formData = this.form.getValue();
    if (!formData) {
      errors.push('Please fill all required fields');
      this.stepValidationErrors.set(errors);
      return false;
    }

    this.stepValidationErrors.set([]);
    return true;
  }

  private validateAreasStep(_errors: string[]): boolean {
    // Areas step is always valid (optional)
    this.stepValidationErrors.set([]);
    return true;
  }

  // Legacy onSubmit method for backward compatibility (not used in multi-step)
  onSubmit(e: ButtonClickEvent) {
    // This method is kept for compatibility but shouldn't be called in multi-step mode
    this.onMainAction(e);
  }
}
